import React, { Fragment, useState } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage, useIntl } from 'react-intl';
import {
	Badge,
	Dropdown,
	DropdownItem,
	DropdownMenu,
	DropdownToggle,
} from 'reactstrap';

import { UserAvatar } from '@techchange/filestack';
import {
	tcBuildSessionURL,
	toSlug,
	SCOPES,
	TECHCHANGE_HELP_PAGE_URL,
} from '@techchange/components';
import { messages } from '../../../i18n';

import LanguageModal from '../../LanguageModal';

import './UserNavDropdownMenu.scss';

const propTypes = {

	/**
	 * The current user.
	 */
	currentUser: PropTypes.shape({

		/**
		 * The user's ID.
		 */
		id: PropTypes.string.isRequired,

		/**
		 * The user's display name.
		 */
		display_name: PropTypes.string.isRequired,

		/**
		 * The user's language preference, if any.
		 */
		language: PropTypes.string,

		/**
		 * The profile pictures for the user.
		 */
		avatars: PropTypes.arrayOf(PropTypes.shape({

			/**
			 * True, if the image is active.
			 */
			active: PropTypes.bool,

			/**
			 * The filstack image url.
			 */
			image: PropTypes.string,

			/**
			 * The filestack security policy.
			 */
			policy: PropTypes.string,

			/**
			 * The filestack security signature.
			 */
			signature: PropTypes.string,

		})),

		/**
		 * The user's timezone (GMT).
		 */
		timezone: PropTypes.string,

	}).isRequired,

	/**
	 * The organization for the current user.
	 */
	myOrganization: PropTypes.shape({

		/**
		 * The organization's title.
		 */
		title: PropTypes.string.isRequired,

		/**
		 * The organization's language setting.
		 */
		language: PropTypes.string.isRequired,

	}).isRequired,

	/**
	 * True, if the current user has admin privileges for the organization.
	 */
	isAdmin: PropTypes.bool,

	/**
	 * True, if the current user has admin privileges for the active course session (if any).
	 */
	isSessionAdmin: PropTypes.bool,

	/*
	 * The active session (if any).
	 */
	session: PropTypes.shape({

		/**
		 * The session ID.
		 */
		id: PropTypes.string.isRequired,

		/**
		 * The session slug.
		 */
		slug: PropTypes.string.isRequired,

		/**
		 * The active course (if any).
		 */
		course: PropTypes.shape({

			/**
			 * The course ID.
			 */
			id: PropTypes.string.isRequired,

			/**
			 * The course title.
			 */
			title: PropTypes.string.isRequired,

		}),

	}),

	/**
	 * The session point total for the current user.
	 */
	sessionPointTotal: PropTypes.shape({

		total: PropTypes.number,

		scope: PropTypes.oneOf([SCOPES.SESSION]).isRequired,

	}),

	/**
	 * True if points are enabled for the session.
	 */
	pointsEnabled: PropTypes.bool,

	/**
	 * The point label for the organization.
	 */
	pointLabel: PropTypes.shape({

		/**
		 * The point label id.
		 */
		id: PropTypes.string,

		/**
		 * The singular label.
		 */
		singular_label: PropTypes.string,

		/**
		 * The plural label.
		 */
		plural_label: PropTypes.string,

	}),

	/**
	 * NOTE: Temporary flag to hide fields related to automatic
	 * translation.
	 */
	hideTranslationFields: PropTypes.bool,

	/**
	 * Callback to handle loading required data for the dropdown menu.
	 */
	onLoadMenuData: PropTypes.func.isRequired,

	/**
	 * Callback to handle updating the user's language preference.
	 */
	onSelectLanguage: PropTypes.func,

	/**
	 * Callback to handle logging out the current user.
	 */
	onLogout: PropTypes.func.isRequired,

};

const UserNavDropdownMenu = (props) => {

	const {
		currentUser,
		myOrganization,
		isAdmin,
		isSessionAdmin,
		session,
		pointsEnabled,
		pointLabel,
		sessionPointTotal,
		hideTranslationFields,
		onLoadMenuData,
		onLogout,
		onSelectLanguage,
	} = props;

	const intl = useIntl();

	// Keep track of the language settings modal state
	const [showLanguageModal, setShowLanguageModal] = useState(false);

	// Keep track of the dropdown menu state
	const [menuState, setMenuState] = useState({ isOpen: false, hasOpened: false });

	// Handle toggling the dropdown menu state
	const toggle = () => {

		// Check if we need to fetch the required data for the dropdown menu
		if (!menuState.hasOpened) {
			onLoadMenuData();
		}

		setMenuState({ isOpen: !menuState.isOpen, hasOpened: true });

	};

	let profileURL = `/members/${toSlug(currentUser.display_name, currentUser.id)}`;

	if (session && session.id) {
		profileURL += `?session_id=${session.id}`;
	}

	return (
		<Dropdown
			id="account-nav-item"
			isOpen={menuState.isOpen}
			toggle={toggle}
			nav
			inNavbar>
			<DropdownToggle
				className={`
					btn btn-outline-neutral tc-icon-button
					dropdown-toggle-profile-picture
					${menuState.isOpen ? 'active' : ''}
				`}
				tag="button"
				title={intl.formatMessage(messages.TC_COMPONENTS_UNIVERSAL_NAVBAR_USER_DROPDOWN_TOGGLE_TITLE_TEXT, { org: myOrganization.title })}
				nav>
				<UserAvatar
					user={currentUser}
					size="40"
					alt={currentUser.display_name}
					rounded />
			</DropdownToggle>
			<DropdownMenu
				data-bs-popper="static"
				end>
				<div className="tc-universal-dropdown-menu-header d-flex flex-column">
					<div className="tc-universal-dropdown-menu-header-title">
						{currentUser.display_name}
					</div>
					<a
						className="user-timezone btn btn-outline-neutral btn-sm fw-normal rounded-pill"
						href="/settings/general"
						title={intl.formatMessage(messages.TC_COMPONENTS_UNIVERSAL_NAVBAR_USER_DROPDOWN_ITEM_TEXT_USER_TIMEZONE)}>
						<i
							aria-hidden="true"
							className="far fa-clock me-1" />
						<span>
							{currentUser.timezone
								? currentUser.timezone
								: intl.formatMessage(messages.TC_COMPONENTS_UNIVERSAL_NAVBAR_USER_DROPDOWN_ITEM_TEXT_USER_NO_TIMEZONE)}
						</span>
					</a>
				</div>
				<DropdownItem divider />
				{/* Session specific dropdown items */}
				{session && session.course &&
					<Fragment>
						<DropdownItem header>
							{session.course.title}
						</DropdownItem>
						<DropdownItem
							id="tc-universal-dropdown-menu-link-progress-page"
							className="d-flex align-items-center"
							tag="a"
							href={`${tcBuildSessionURL(session)}/progress/${currentUser.id}`}>
							<FormattedMessage
								{...messages.TC_COMPONENTS_UNIVERSAL_NAVBAR_USER_DROPDOWN_ITEM_TEXT_USER_SESSION_PROGRESS} />
						</DropdownItem>
						{pointsEnabled && pointLabel &&
							<DropdownItem
								id="tc-universal-dropdown-menu-link-profile-techpoints-page"
								className="d-flex align-items-center"
								tag="a"
								href={profileURL}>
								{pointLabel.plural_label}
								{sessionPointTotal &&
									<Badge
										color="primary"
										className="ms-2">
										{sessionPointTotal.total}
									</Badge>
								}
							</DropdownItem>
						}
						{isSessionAdmin &&
							<DropdownItem
								id="tc-universal-dropdown-menu-link-settings-page"
								tag="a"
								href={`${tcBuildSessionURL(session)}/settings`}>
								<FormattedMessage {...messages.TC_COMPONENTS_UNIVERSAL_NAVBAR_USER_DROPDOWN_ITEM_TEXT_USER_SESSION_SETTINGS} />
							</DropdownItem>
						}
						<DropdownItem divider />
					</Fragment>
				}
				{/* Platform wide dropdown items */}
				<DropdownItem
					id="tc-universal-dropdown-menu-link-dashboard-page"
					tag="a"
					href="/dashboard">
					<FormattedMessage {...messages.TC_COMPONENTS_UNIVERSAL_NAVBAR_USER_DROPDOWN_ITEM_TEXT_TO_DASHBOARD} />
				</DropdownItem>
				<DropdownItem
					id="tc-universal-dropdown-menu-link-profile-page"
					tag="a"
					href={profileURL}>
					<FormattedMessage {...messages.TC_COMPONENTS_UNIVERSAL_NAVBAR_USER_DROPDOWN_ITEM_TEXT_TO_PROFILE} />
				</DropdownItem>
				<DropdownItem
					id="tc-universal-dropdown-menu-link-user-settings-page"
					tag="a"
					href="/settings">
					<FormattedMessage {...messages.TC_COMPONENTS_UNIVERSAL_NAVBAR_USER_DROPDOWN_ITEM_TEXT_TO_SETTINGS} />
				</DropdownItem>
				<DropdownItem
					id="tc-universal-dropdown-menu-link-change-language-selector"
					onClick={() => setShowLanguageModal((prev) => !prev)}>
					<FormattedMessage {...messages.TC_COMPONENTS_UNIVERSAL_NAVBAR_USER_DROPDOWN_ITEM_TEXT_LANGUAGE_SETTINGS} />
				</DropdownItem>
				{(isAdmin || isSessionAdmin) &&
					<DropdownItem divider />
				}
				{isAdmin &&
					<DropdownItem
						id="tc-universal-dropdown-menu-link-admin-page"
						tag="a"
						href="/admin">
						<FormattedMessage {...messages.TC_COMPONENTS_UNIVERSAL_NAVBAR_USER_DROPDOWN_ITEM_TEXT_TO_ADMIN_DASHBOARD} />
					</DropdownItem>
				}
				{(isAdmin || isSessionAdmin) &&
					<DropdownItem
						id="tc-universal-dropdown-menu-link-help-page"
						tag="a"
						href={TECHCHANGE_HELP_PAGE_URL}
						rel="noopener noreferrer"
						target="_blank">
						<span>
							<FormattedMessage {...messages.TC_COMPONENTS_UNIVERSAL_NAVBAR_USER_DROPDOWN_ITEM_TEXT_TO_DOCUMENTATION} />
							<i
								className="fa fa-external-link ms-2"
								aria-hidden="true" />
						</span>
					</DropdownItem>
				}
				<DropdownItem divider />
				<DropdownItem
					id="tc-universal-dropdown-menu-link-log-out-page"
					onClick={onLogout}>
					<FormattedMessage {...messages.TC_COMPONENTS_UNIVERSAL_NAVBAR_USER_DROPDOWN_ITEM_TEXT_USER_LOG_OUT} />
				</DropdownItem>
			</DropdownMenu>
			<LanguageModal
				key={showLanguageModal}
				currentUser={currentUser}
				isOpen={showLanguageModal}
				hideTranslationFields={hideTranslationFields}
				toggle={() => setShowLanguageModal((prev) => !prev)}
				onSelectLanguage={onSelectLanguage} />
		</Dropdown>
	);

};

UserNavDropdownMenu.propTypes = propTypes;

export default UserNavDropdownMenu;
